import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useApiClient } from "../api/client";
import { readSessionProfile, clearSessionProfile } from "../api/profile";

/**
 * Mounts at the App root. Runs once when auth transitions to signed-in:
 * if the user has a sessionStorage profile and the backend has none yet,
 * POST the sessionStorage profile to /api/profile and clear sessionStorage.
 *
 * If the backend already has a profile (e.g. user signed in on another
 * device first), sessionStorage is treated as stale and cleared without
 * overwriting the backend.
 */
export default function ProfileMigrator() {
  const { isLoaded, isSignedIn } = useAuth();
  const api = useApiClient();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || ranRef.current) return;
    const sessionProfile = readSessionProfile();
    if (!sessionProfile) return;
    ranRef.current = true;
    (async () => {
      try {
        await api.get("/profile");
        clearSessionProfile();
      } catch (err) {
        if (err?.response?.status === 404) {
          try {
            await api.put("/profile", sessionProfile);
            clearSessionProfile();
          } catch {
            ranRef.current = false;
          }
        }
      }
    })();
  }, [isLoaded, isSignedIn, api]);

  return null;
}
